(function () {
  'use strict';

  /*
   * PEVELL collection-card colour authority.
   *
   * The active swatch owns both:
   *   1. which small image is active
   *   2. which image is displayed in the main card
   *
   * Filter/LIFO code is not recalculated here. This file only mirrors the
   * already-established active colour and protects it from legacy handlers
   * that try to write a different image after the filter state is applied.
   */
  if (window.__PEVELL_ACTIVE_SWATCH_MAIN_STARTED__) return;
  window.__PEVELL_ACTIVE_SWATCH_MAIN_STARTED__ = true;

  var lastFilterSignature = null;

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function selectedColors() {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get('pv_color') || '';
    if (!raw) return [];
    return raw.split(',').map(normalize).filter(Boolean);
  }

  function filterSignature() {
    return selectedColors().join('|');
  }

  function getSwatches(card) {
    return Array.prototype.slice.call(card.querySelectorAll('.pv-gc__sw[data-pv-sw-id]'));
  }

  function getColor(swatch) {
    return normalize(swatch.getAttribute('aria-label') || swatch.title || '');
  }

  function getImage(swatch) {
    if (!swatch) return '';
    var src = swatch.getAttribute('data-pv-sw-img') || '';
    if (src) return src;
    var image = swatch.querySelector('img');
    return image ? (image.currentSrc || image.src || '') : '';
  }

  function findSwatchByColor(card, color) {
    if (!color) return null;
    var swatches = getSwatches(card);
    for (var i = 0; i < swatches.length; i++) {
      if (getColor(swatches[i]) === color) return swatches[i];
    }
    return null;
  }

  function getCurrentActive(card) {
    var swatches = getSwatches(card);
    for (var i = 0; i < swatches.length; i++) {
      if (swatches[i].classList.contains('is-active') || swatches[i].getAttribute('aria-pressed') === 'true') {
        return swatches[i];
      }
    }
    return swatches[0] || null;
  }

  function getDesiredActive(card) {
    var signature = filterSignature();

    /* A manual click is stronger than the URL filter until the URL filter
       itself changes. This is what makes clicking a visible/prompted swatch
       immediately authoritative. */
    if (card.dataset.pvManualActiveSignature === signature && card.dataset.pvManualActiveColor) {
      var manual = findSwatchByColor(card, card.dataset.pvManualActiveColor);
      if (manual) return manual;
    }

    var colors = selectedColors();
    if (colors.length) {
      /* Last selected filter colour is the active colour. */
      for (var i = colors.length - 1; i >= 0; i--) {
        var selected = findSwatchByColor(card, colors[i]);
        if (selected) return selected;
      }
    }

    /* Keep the server-selected price-sort colour authoritative. */
    var sortedColor = normalize(card.dataset.pvSortActiveColor || '');
    if (sortedColor) {
      var sorted = findSwatchByColor(card, sortedColor);
      if (sorted) return sorted;
    }

    return getCurrentActive(card);
  }

  function setActive(card, active) {
    if (!active) return;

    var swatches = getSwatches(card);
    var changed = false;

    for (var i = 0; i < swatches.length; i++) {
      var on = swatches[i] === active;
      if (swatches[i].classList.contains('is-active') !== on) changed = true;
      if ((swatches[i].getAttribute('aria-pressed') === 'true') !== on) changed = true;
    }

    if (!changed) return;

    card.dataset.pvActiveAuthorityWriting = 'true';
    try {
      for (var j = 0; j < swatches.length; j++) {
        var enabled = swatches[j] === active;
        swatches[j].classList.toggle('is-active', enabled);
        swatches[j].setAttribute('aria-pressed', enabled ? 'true' : 'false');
      }
    } finally {
      delete card.dataset.pvActiveAuthorityWriting;
    }
  }

  function setMainImage(card, active) {
    if (!active) return;

    var main = card.querySelector('[data-pv-card-main]');
    var src = getImage(active);
    if (!main || !src) return;

    var key = (active.getAttribute('data-pv-sw-id') || '') + '|' + getColor(active) + '|' + src;

    /* One direct image assignment. No Image() preload and no timeout. */
    if (main.getAttribute('data-pv-active-swatch-key') !== key) {
      main.setAttribute('data-pv-active-swatch-key', key);
      /* Pevell speed: the card already shows this colour, so keep its responsive srcset and lazy loading. */
      if (main.getAttribute('src') === src && main.hasAttribute('srcset')) {
        main.style.removeProperty('opacity');
        return;
      }
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.removeAttribute('loading');
      main.style.removeProperty('opacity');
      if (main.getAttribute('src') !== src) main.src = src;
      return;
    }

    /* Legacy code may change only the source or opacity after the active state
       is already correct. Reconcile those two properties without a second
       image-selection pass. */
    main.style.removeProperty('opacity');
    if (main.getAttribute('src') !== src) main.src = src;
  }

  function syncCard(card) {
    if (!card) return;
    var active = getDesiredActive(card);
    if (!active) return;
    setActive(card, active);
    setMainImage(card, active);
  }

  function bindCard(card) {
    if (!card) return;

    if (card.dataset.pvActiveSwatchMainBound === 'true') {
      syncCard(card);
      return;
    }

    card.dataset.pvActiveSwatchMainBound = 'true';

    var observer = new MutationObserver(function (mutations) {
      if (card.dataset.pvActiveAuthorityWriting === 'true') return;

      var needsSync = false;
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes') return;

        if (
          mutation.target.classList &&
          mutation.target.classList.contains('pv-gc__sw') &&
          (mutation.attributeName === 'class' || mutation.attributeName === 'aria-pressed')
        ) {
          needsSync = true;
          return;
        }

        var main = card.querySelector('[data-pv-card-main]');
        if (
          main &&
          mutation.target === main &&
          (mutation.attributeName === 'src' ||
           mutation.attributeName === 'srcset' ||
           mutation.attributeName === 'sizes' ||
           mutation.attributeName === 'style')
        ) {
          needsSync = true;
        }
      });

      if (needsSync) syncCard(card);
    });

    var main = card.querySelector('[data-pv-card-main]');
    if (main) {
      observer.observe(main, {
        attributes: true,
        attributeFilter: ['src', 'srcset', 'sizes', 'style']
      });
    }

    getSwatches(card).forEach(function (swatch) {
      observer.observe(swatch, {
        attributes: true,
        attributeFilter: ['class', 'aria-pressed']
      });
    });

    syncCard(card);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-pv-grid-card]').forEach(bindCard);
  }

  function clearManualOverridesIfFilterChanged() {
    var next = filterSignature();
    if (next === lastFilterSignature) return;

    lastFilterSignature = next;
    document.querySelectorAll('[data-pv-grid-card]').forEach(function (card) {
      delete card.dataset.pvManualActiveColor;
      delete card.dataset.pvManualActiveSignature;
    });

    /* Run once for the actual filter transition. This is not a continuous
       colour scan. */
    init(document);
  }

  document.addEventListener('click', function (event) {
    var swatch = event.target.closest && event.target.closest('.pv-gc__sw[data-pv-sw-id]');
    if (!swatch) return;

    var card = swatch.closest('[data-pv-grid-card]');
    if (!card) return;

    var signature = filterSignature();
    card.dataset.pvManualActiveColor = getColor(swatch);
    card.dataset.pvManualActiveSignature = signature;

    /* Capture-phase ownership happens before the older collection click
       handler. The MutationObserver then protects this state if that older
       handler writes another active class or image during the same click. */
    syncCard(card);
  }, true);

  function installHistoryHook(name) {
    var original = history[name];
    if (!original || original.__pvActiveSwatchHooked) return;

    function wrapped() {
      var result = original.apply(this, arguments);
      queueMicrotask(clearManualOverridesIfFilterChanged);
      return result;
    }

    wrapped.__pvActiveSwatchHooked = true;
    history[name] = wrapped;
  }

  installHistoryHook('pushState');
  installHistoryHook('replaceState');

  window.addEventListener('popstate', function () {
    queueMicrotask(clearManualOverridesIfFilterChanged);
  });

  window.addEventListener('hashchange', function () {
    queueMicrotask(clearManualOverridesIfFilterChanged);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    clearManualOverridesIfFilterChanged();
  });

  function start() {
    lastFilterSignature = filterSignature();
    init(document);

    if (document.body && !window.__PEVELL_ACTIVE_SWATCH_MAIN_OBSERVER__) {
      var gridObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (!node || node.nodeType !== 1) return;

            if (node.matches && node.matches('[data-pv-grid-card]')) {
              bindCard(node);
            } else if (node.querySelectorAll) {
              node.querySelectorAll('[data-pv-grid-card]').forEach(bindCard);
            }
          });
        });
      });

      gridObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      window.__PEVELL_ACTIVE_SWATCH_MAIN_OBSERVER__ = gridObserver;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('load', function () {
    init(document);
  }, { once: true });
})();